import React, { useState, useCallback, useMemo } from "react";
import { CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, RefreshCw, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDashboardData } from "@/services/dashboardDataService";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import "./widgets.css";
import { cleanDateAndTimeObject } from "@shared/renderDate";

// Visually hidden but accessible styles
const visuallyHidden: React.CSSProperties = {
  border: '0',
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: '0',
  position: 'absolute',
  width: '1px',
  whiteSpace: 'nowrap',
};

interface CategoryData {
  count: number;
  percentage: number;
}

interface MetricsData {
  totalTasks: number;
  totalOverdue?: number;
  totalCompleted?: number;
  overduePercentage?: number;
  byCategory: {
    [key: string]: CategoryData;
  };
}

interface OverdueTasksPercentageData {
  metrics: {
    currentStatus: MetricsData;
    completionPerformance: MetricsData;
  };
  lastUpdated?: {
    toDate?: () => Date;
  };
  error?: boolean;
  errorMessage?: string;
}

// Types for view configuration
interface ViewCategory {
  key: string;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

interface ViewConfig {
  title: string;
  description: string;
  categories: ViewCategory[];
}

// View configuration
const viewConfig: Record<'currentStatus' | 'completionPerformance', ViewConfig> = {
  currentStatus: {
    title: 'Current Status',
    description: 'Shows tasks by their current status (upcoming, past due, or very late)',
    categories: [
      { 
        key: 'upcoming', 
        label: 'Upcoming', 
        description: 'Tasks due in the future',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-200'
      },
      { 
        key: 'pastDue', 
        label: 'Past Due', 
        description: 'Tasks 1-29 days overdue',
        color: 'text-orange-500',
        bgColor: 'bg-orange-100',
        borderColor: 'border-orange-200'
      },
      { 
        key: 'veryLate', 
        label: 'Very Late', 
        description: 'Tasks 30+ days overdue',
        color: 'text-red-500',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-200'
      }
    ]
  },
  completionPerformance: {
    title: 'Completion Performance',
    description: 'Shows how many tasks were completed on time or late',
    categories: [
      { 
        key: 'onTime', 
        label: 'On Time', 
        description: 'Completed on or before due date',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-200'
      },
      { 
        key: 'slightlyLate', 
        label: 'Slightly Late', 
        description: 'Completed 1-29 days after due date',
        color: 'text-amber-500',
        bgColor: 'bg-amber-100',
        borderColor: 'border-amber-200'
      },
      { 
        key: 'veryLate', 
        label: 'Very Late', 
        description: 'Completed 30+ days after due date',
        color: 'text-red-500',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-200'
      }
    ]
  }
};

const OverdueTasksCounterWidget: React.FC = () => {
  // All hooks at the top level
  const [view, setView] = useState<'currentStatus' | 'completionPerformance'>('currentStatus');
  const { data, isLoading, error, refetch } = useDashboardData('overdue-tasks-percentage');
  
  // Report widget status to dashboard context
  useWidgetStatusReporter('overdue-tasks-percentage', isLoading, error, !!data?.metrics);
  
  const toggleView = useCallback(() => {
    setView(prevView => 
      prevView === 'currentStatus' ? 'completionPerformance' : 'currentStatus'
    );
  }, []);

    // Use useCallback to prevent unnecessary re-renders
    const handleCardClick = useCallback((e: React.MouseEvent) => {
      // Prevent the click from being interpreted as a drag start by react-grid-layout
      e.stopPropagation();
      e.preventDefault();
      toggleView();
    }, [toggleView]);
  
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        toggleView();
      }
    }, [toggleView]);

  // Memoize derived state to prevent unnecessary recalculations
  const { metrics, isCurrentStatusView, currentView, totalCount, byCategory } = useMemo(() => {
    if (isLoading || !data?.metrics) {
      return { 
        metrics: null, 
        isCurrentStatusView: false, 
        currentView: viewConfig.currentStatus,
        totalCount: 0,
        byCategory: {}
      };
    }

    const viewMetrics = data.metrics[view as keyof typeof data.metrics];
    const isCurrentView = view === 'currentStatus';
    const currentViewConfig = viewConfig[view] || viewConfig.currentStatus;
    
    return {
      metrics: viewMetrics,
      isCurrentStatusView: isCurrentView,
      currentView: currentViewConfig,
      totalCount: isCurrentView ? (viewMetrics?.totalTasks || 0) : (viewMetrics?.totalCompleted || 0),
      byCategory: {
        upcoming: { count: 0, percentage: 0 },
        pastDue: { count: 0, percentage: 0 },
        veryLate: { count: 0, percentage: 0 },
        onTime: { count: 0, percentage: 0 },
        slightlyLate: { count: 0, percentage: 0 },
        ...viewMetrics?.byCategory
      }
    };
  }, [data, isLoading, view]);

  // Handle loading state
  if (isLoading || !data || !data.metrics) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center p-4" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-4">Overdue Tasks</h3>
        <div className="w-full space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex flex-row justify-between w-3/4 mx-auto">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </div>
      </CardContent>
    );
  }

  // Handle error state
  if (error || data.error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center p-4" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">Overdue Tasks</h3>
        <Alert variant="destructive" className="w-full max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-2">
            <span>{error?.toString() || data.errorMessage || 'Failed to load overdue tasks data'}</span>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 self-start"
              onClick={() => refetch()}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </CardContent>
    );
  }

  return (
    <CardContent 
      className="h-full w-full flex flex-col p-4 cursor-pointer hover:bg-accent/5 transition-colors select-none" 
      style={{ 
        containerType: 'inline-size',
        // Improve click responsiveness
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
      }}
      onClick={handleCardClick}
      onMouseDown={(e) => {
        // Prevent text selection on drag
        e.preventDefault();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Task Status</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-sm">{currentView.description}</p>
                <p className="text-xs mt-1 text-muted-foreground">Click anywhere to toggle view</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-1 rounded-md p-0.5" style={visuallyHidden}>
          <Toggle
            pressed={view === 'currentStatus'}
            onPressedChange={() => setView('currentStatus')}
            className="h-7 px-2 text-xs data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
            aria-label="Show current status view"
          >
            Status
          </Toggle>
          <Toggle
            pressed={view === 'completionPerformance'}
            onPressedChange={() => setView('completionPerformance')}
            className="h-7 px-2 text-xs data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
            aria-label="Show completion performance view"
          >
            Performance
          </Toggle>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => refetch()}
            title="Refresh data"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      
      <div className="w-full flex flex-col gap-3">
        {currentView.categories.map((category) => {
          const categoryData = byCategory[category.key];
          if (!categoryData) return null;
          
          return (
            <div 
              key={category.key}
              className={`flex flex-row justify-between items-center w-full p-2 rounded-md ${category.bgColor} ${category.borderColor} border`}
            >
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${category.color}`}>
                  {category.label}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground cursor-help">
                        {category.description}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{category.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold ${category.color}`}>
                  {categoryData.percentage.toFixed(1)}%
                </span>
                <span className="text-xs text-muted-foreground">
                  ({categoryData.count}{totalCount ? `/${totalCount}` : ''})
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      {data.lastUpdated && (
        <div className="mt-3 text-xs text-muted-foreground text-center">
          Updated: {cleanDateAndTimeObject(data.lastUpdated).toLocaleTimeString()}
        </div>
      )}
    </CardContent>
  );
};

export default OverdueTasksCounterWidget;
